import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  SearchBox,
  useMobile,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { studentsPetitionsListFilter } from '../../../../api/requests/studentPetitions';
import { useValidations } from '../../../../hooks/useValidations';
import { SearchParams } from '../../types/Petitions';

const dictPrefix = 'studentPetitions.received.searchControls';

const DEFAULT_VALUES = {
  search: '',
  studyPlanId: null,
  status: null,
  campusId: null,
  petitionTypeId: null,
};

interface FormValues {
  search: string;
  studyPlanId: OutlinedSelectOptionType | null;
  status: OutlinedSelectOptionType | null;
  campusId: OutlinedSelectOptionType | null;
  petitionTypeId: OutlinedSelectOptionType | null;
}

type onSearch = {
  searchParams: SearchParams;
  page: number;
};

interface SearchControlsProps {
  onSearch?: ({ searchParams, page }: onSearch) => void;
  onClear?: () => void;
  setFilters?: (state: any) => void;
}

export function SearchControls({
  onSearch = () => null,
  onClear = () => null,
  setFilters = (state: any) => null,
}: SearchControlsProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { validateMinLength } = useValidations();

  const [states, setStates] = useState<OutlinedSelectOptionType[]>([]);
  const [studyPlans, setStudyPlans] = useState<OutlinedSelectOptionType[]>([]);
  const [campuses, setCampuses] = useState<OutlinedSelectOptionType[]>([]);
  const [petitionTypes, setPetitionTypes] = useState<
    OutlinedSelectOptionType[]
  >([]);

  const { handleSubmit, control, reset, watch } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const [
    searchWatch,
    statusWatch,
    studyPlanIdWatch,
    campusIdWatch,
    petitionTypeIdWatch,
  ] = watch(['search', 'status', 'studyPlanId', 'campusId', 'petitionTypeId']);

  useEffect(() => {
    const searchParams = {
      search: searchWatch?.trim() !== '' ? searchWatch : undefined,
      status: statusWatch?.value as number,
      studyPlanId: studyPlanIdWatch?.value as number,
      campusId: campusIdWatch?.value as number,
      petitionTypeId: petitionTypeIdWatch?.value as number,
    };

    setFilters({
      ...searchParams,
      hasFilters: Object.values(searchParams).some((value) => value !== null),
    });
  }, [
    searchWatch,
    statusWatch,
    studyPlanIdWatch,
    campusIdWatch,
    petitionTypeIdWatch,
    setFilters,
  ]);

  const handleSearch = (values: FormValues) => {
    const searchParams = {
      search: values.search?.trim() !== '' ? values.search : undefined,
      status: values.status?.value as string,
      studyPlanId: values.studyPlanId?.value as number,
      campusId: values.campusId?.value as number,
      petitionTypeId: values.petitionTypeId?.value as string,
      hasFilters: Object.values(values).some((value) => value !== null),
    };

    onSearch({ searchParams, page: 0 });
  };

  const handleClear = () => {
    reset(DEFAULT_VALUES);
    onClear();
    onSearch({ searchParams: {}, page: 0 });
  };

  const getFilters = useCallback(async () => {
    const { data } = await studentsPetitionsListFilter();
    if (data) {
      setStates(
        data.status.map((value: string) => ({
          label: t(`${dictPrefix}.status.${value}`),
          value: value,
        })),
      );
      setStudyPlans(
        data.studyPlans.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      setCampuses(
        data.campuses.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );
      setPetitionTypes(
        data.petitionTypes.map(({ id: value, name: label }) => ({
          label,
          value,
        })),
      );

      reset();
    }
  }, [t, reset]);

  useEffect(() => {
    getFilters();
  }, [getFilters]);

  return (
    <form
      onSubmit={handleSubmit(handleSearch)}
      style={{ gap: isMobile ? 0 : 4 }}
    >
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="studyPlanId"
            label={t(`${dictPrefix}.studyPlan`)}
            placeholder={t(`${dictPrefix}.studyPlan`)}
            control={control}
            options={studyPlans}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <SearchBox
            name="search"
            label={t(`${dictPrefix}.nameOrId`)}
            placeholder={t(`${dictPrefix}.nameOrIdPlaceholder`)}
            control={control}
            rules={{
              validate: {
                minLength: validateMinLength(3),
              },
            }}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="campusId"
            label={t(`${dictPrefix}.campus`)}
            placeholder={t(`${dictPrefix}.campusPlaceholder`)}
            control={control}
            options={campuses}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="status"
            label={t(`${dictPrefix}.state`)}
            placeholder={t(`${dictPrefix}.state`)}
            control={control}
            options={states}
          />
        </Col>

        <Col xs={12} md={6} lg={4}>
          <OutlinedSelect
            name="petitionTypeId"
            label={t(`${dictPrefix}.petitionType`)}
            placeholder={t(`${dictPrefix}.petitionTypePlaceholder`)}
            control={control}
            options={petitionTypes}
          />
        </Col>

        <Col
          xs={12}
          md={{ size: 3, offset: 0 }}
          lg={{ size: 2, offset: 0 }}
          style={{ marginTop: isMobile ? 0 : '1.5rem' }}
        >
          <Button
            type="submit"
            text={t(`common.actions.search`)}
            size="md"
            fullwidth
          />
        </Col>

        <Col
          xs={12}
          md={{ size: 3 }}
          lg={2}
          style={{ marginTop: isMobile ? '1rem' : '1.5rem' }}
        >
          <Button
            text={t(`common.actions.clean`)}
            size="md"
            fullwidth
            outlined
            onClick={handleClear}
          />
        </Col>
      </Row>
    </form>
  );
}
